import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FiEdit2, FiMoreVertical, FiTrash2 } from 'react-icons/fi';
import { SiOpenaccess } from 'react-icons/si';

import { DeleteModal } from '@/components/deleteModal/deleteModal';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { Text } from '@/components/typography/text';
import { useSecretDelete } from '@/hooks/mutations/useSecretMutations';
import { useContractCountBySecret } from '@/hooks/queries/useDataContracts';
import { secretsKeys, useSecretDetailQuery } from '@/hooks/queries/useSecrets';
import { SecretListItem, SecretTypeEnum } from '@/types/secret';

import { ManageAccessModal } from './manageAccessModal';
import { SecretModal } from './secretModal';

type Props = {
  secret: SecretListItem;
};

export const SecretActionMenu = ( { secret }: Props ) => {
  const [ showDeleteConfirm, setShowDeleteConfirm ] = useState( false );
  const [ showUpdateModal, setShowUpdateModal ] = useState( false );
  const [ showManageAccessModal, setShowManageAccessModal ] = useState( false );
  const queryClient = useQueryClient();
  const deleteMutation = useSecretDelete();

  const isAgentType = secret.type === SecretTypeEnum.AGENT || secret.type === SecretTypeEnum.EVALS;

  // Fetch usage counts only when the delete modal is requested and before deletion
  const shouldFetchCounts = showDeleteConfirm && !deleteMutation.isSuccess;
  const { data: secretDetail, isSuccess: detailLoaded } = useSecretDetailQuery( shouldFetchCounts && isAgentType ? secret.id : undefined );
  const { data: contractCount, isSuccess: contractCountLoaded } = useContractCountBySecret( shouldFetchCounts && !isAgentType ? secret.name : undefined );

  // Defer opening the modal until the count has loaded
  const isCountReady = isAgentType ? detailLoaded : contractCountLoaded;
  const isDeleteModalOpen = showDeleteConfirm && ( isCountReady || deleteMutation.isSuccess );

  const constellationCount = secretDetail?.agentMappings?.length ?? 0;
  const entityCount = isAgentType ? constellationCount : ( contractCount ?? 0 );

  const deleteWarning = (
    <Text as="p" size="md">
      This will permanently delete <strong>{ secret.name }</strong>.
      <br /><br />
      { isAgentType
        ? <>Please check that this will not break the <strong>{ entityCount } constellation{ entityCount !== 1 ? 's' : '' } and associated agents</strong> it is assigned to.</>
        : <>Please check that this will not break the <strong>{ entityCount } data platform entit{ entityCount !== 1 ? 'ies' : 'y' }</strong> it is associated with.</>}
    </Text>
  );

  return (
    <>
      <Menu
        icon={ <Icon icon={ FiMoreVertical } /> }
        variant='menu'
        data-testid="secret-action-menu"
      >
        <MenuItem
          key="update"
          value="Update secret"
          onClick={ () => setShowUpdateModal( true ) }
          data-testid="secret-menu-update"
          leadingIcon={ <Icon icon={ FiEdit2 } size="sm" /> }
        />
        {isAgentType && (
          <MenuItem
            key="manage"
            value="Manage access"
            onClick={ () => setShowManageAccessModal( true ) }
            data-testid="secret-menu-manage"
            leadingIcon={ <Icon icon={ SiOpenaccess } size="sm" /> }
          />
        )}
        <MenuItem
          key="delete"
          value="Delete secret"
          onClick={ () => setShowDeleteConfirm( true ) }
          data-testid="secret-menu-delete"
          leadingIcon={ <Icon icon={ FiTrash2 } size="sm" /> }
        />
      </Menu>

      {showUpdateModal && (
        <SecretModal
          mode="update"
          isOpen={ showUpdateModal }
          onClose={ () => setShowUpdateModal( false ) }
          secret={ secret }
        />
      )}

      {showManageAccessModal && (
        <ManageAccessModal
          isOpen={ showManageAccessModal }
          onClose={ () => setShowManageAccessModal( false ) }
          secret={ secret }
        />
      )}

      <DeleteModal
        isOpen={ isDeleteModalOpen }
        onClose={ () => {
          setShowDeleteConfirm( false );
          if ( deleteMutation.isSuccess ) {
            queryClient.invalidateQueries( { queryKey: secretsKeys.list() } );
          }
          deleteMutation.reset();
        } }
        onDelete={ () => deleteMutation.mutateAsync( { secretId: secret.id } ) }
        entityName={ secret.name }
        entityType="secret"
        overrideWarning={ deleteWarning }
      />
    </>
  );
};
